import { useEffect, useRef, useState } from "react";
import { Pencil, X, Plus, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { savedEmailsApi } from "@/lib/api";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const cleanText = (v) =>
  String(v ?? "")
    .replace(/[\u00a0\u200b\u200c\u200d\ufeff]/g, " ")
    .trim();

/**
 * Inline-list saved-emails picker.
 *
 * Shows a permanent list of saved emails (label + email + check + edit + delete).
 * The "+ Ajouter un destinataire" button at the bottom transforms in-place into
 * an inline form. Submitting (Enter or button) :
 *   • POSTs the new email (or PUTs on edit)
 *   • Appends the row to the list IMMEDIATELY (optimistic)
 *   • Auto-checks the row → it joins selectedEmails
 *   • Restores the "+ Ajouter" button
 *
 * Props:
 *   selectedEmails    : string[]  — controlled selection
 *   onSelectionChange : (emails)  — typically setExtraEmails / setResendExtras
 */
export default function SavedEmailsPicker({ selectedEmails = [], onSelectionChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Inline form state
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [label, setLabel] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  // DOM refs — used at submit-time for race-proof reads
  const labelRef = useRef(null);
  const emailRef = useRef(null);

  // Always-fresh selection reference (avoids stale closure across async awaits)
  const selectedRef = useRef(selectedEmails);
  useEffect(() => {
    selectedRef.current = selectedEmails;
  }, [selectedEmails]);

  const load = async () => {
    setLoading(true);
    try {
      const data = await savedEmailsApi.list();
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      // Do NOT wipe items on transient errors
      console.error("[SavedEmailsPicker] load failed", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const isChecked = (em) =>
    (selectedEmails || []).some((e) => e.toLowerCase() === em.toLowerCase());

  const toggle = (em) => {
    const current = selectedRef.current || [];
    const lc = em.toLowerCase();
    if (current.some((e) => e.toLowerCase() === lc)) {
      onSelectionChange?.(current.filter((e) => e.toLowerCase() !== lc));
    } else {
      onSelectionChange?.([...current, em]);
    }
  };

  const resetForm = () => {
    setAdding(false);
    setEditingId(null);
    setLabel("");
    setEmail("");
  };

  const handleStartEdit = (it) => {
    setAdding(true);
    setEditingId(it.id);
    setLabel(it.label);
    setEmail(it.email);
  };

  const handleSave = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const lbl = cleanText(labelRef.current?.value ?? label);
    const eml = cleanText(emailRef.current?.value ?? email);
    if (!lbl) {
      toast.error("Le libellé est obligatoire");
      labelRef.current?.focus();
      return;
    }
    if (!eml || !EMAIL_RE.test(eml)) {
      toast.error("Email invalide", {
        description: 'Format attendu : "nom@domaine.com"',
      });
      emailRef.current?.focus();
      return;
    }
    setLabel(lbl);
    setEmail(eml);
    setBusy(true);
    try {
      if (editingId) {
        const oldEmail = items.find((i) => i.id === editingId)?.email || "";
        const saved = await savedEmailsApi.update(editingId, { label: lbl, email: eml });
        toast.success("Email mis à jour");
        setItems((prev) =>
          (prev || [])
            .map((i) => (i.id === saved.id ? saved : i))
            .sort((a, b) => a.label.localeCompare(b.label))
        );
        // Keep the row in the selection if the user had checked it before
        if (oldEmail && oldEmail.toLowerCase() !== saved.email.toLowerCase()) {
          const current = selectedRef.current || [];
          const wasChecked = current.some(
            (e) => e.toLowerCase() === oldEmail.toLowerCase()
          );
          if (wasChecked) {
            onSelectionChange?.([
              ...current.filter((e) => e.toLowerCase() !== oldEmail.toLowerCase()),
              saved.email,
            ]);
          }
        }
      } else {
        const created = await savedEmailsApi.create({ label: lbl, email: eml });
        toast.success(`${created.label} ajouté à la liste`);
        // Optimistic local insert (guaranteed visible)
        setItems((prev) => {
          const filtered = (prev || []).filter(
            (i) =>
              i.id !== created.id &&
              i.email.toLowerCase() !== created.email.toLowerCase()
          );
          return [...filtered, created].sort((a, b) =>
            a.label.localeCompare(b.label)
          );
        });
        // Auto-tick — read freshest selection via ref
        const current = selectedRef.current || [];
        const alreadyIn = current.some(
          (e) => e.toLowerCase() === created.email.toLowerCase()
        );
        if (!alreadyIn) {
          onSelectionChange?.([...current, created.email]);
        }
      }
      resetForm();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (it) => {
    if (!window.confirm(`Supprimer "${it.label} — ${it.email}" ?`)) return;
    try {
      await savedEmailsApi.remove(it.id);
      toast.success("Email supprimé");
      setItems((prev) => (prev || []).filter((i) => i.id !== it.id));
      const current = selectedRef.current || [];
      if (current.some((e) => e.toLowerCase() === it.email.toLowerCase())) {
        onSelectionChange?.(
          current.filter((e) => e.toLowerCase() !== it.email.toLowerCase())
        );
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  const checkedCount = (selectedEmails || []).filter((e) =>
    items.some((i) => i.email.toLowerCase() === e.toLowerCase())
  ).length;

  return (
    <div data-testid="saved-emails-picker" className="space-y-2">
      <div className="flex items-baseline justify-between gap-2">
        <div className="text-sm font-medium">Destinataires supplémentaires</div>
        {checkedCount > 0 && (
          <div className="text-xs text-[var(--vidoc-primary)] font-medium">
            {checkedCount} sélectionné{checkedCount > 1 ? "s" : ""}
          </div>
        )}
      </div>

      <div
        data-testid="extra-emails-panel"
        className="rounded-xl bg-white border border-[var(--vidoc-border)] divide-y divide-[var(--vidoc-border)] overflow-hidden"
      >
        {loading ? (
          <div className="px-4 py-3 text-xs text-[var(--vidoc-text-secondary)]">
            Chargement…
          </div>
        ) : items.length === 0 && !adding ? (
          <div className="px-4 py-3 text-xs text-[var(--vidoc-text-secondary)] text-center">
            Aucun destinataire mémorisé.<br />
            Ajoutez un contact récurrent (ADV, CP, comptable…) ci-dessous.
          </div>
        ) : (
          items.map((it) => {
            const checked = isChecked(it.email);
            return (
              <div
                key={it.id}
                data-testid={`saved-email-row-${it.id}`}
                className={`flex items-center gap-2 px-3 py-2.5 ${
                  checked ? "bg-[var(--vidoc-bg-secondary)]" : ""
                } hover:bg-[var(--vidoc-bg-secondary)]`}
              >
                <label className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer">
                  <input
                    data-testid={`saved-email-toggle-${it.id}`}
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(it.email)}
                    className="w-5 h-5 accent-[var(--vidoc-primary)] touch-target shrink-0"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{it.label}</div>
                    <div className="text-xs text-[var(--vidoc-text-secondary)] truncate">
                      {it.email}
                    </div>
                  </div>
                </label>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    data-testid={`saved-email-edit-${it.id}`}
                    onClick={() => handleStartEdit(it)}
                    className="p-1.5 rounded-lg text-[var(--vidoc-text-secondary)] hover:bg-white"
                    title="Modifier"
                    aria-label="Modifier"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    data-testid={`saved-email-delete-${it.id}`}
                    onClick={() => handleDelete(it)}
                    className="p-1.5 rounded-lg text-[var(--vidoc-danger)] hover:bg-red-50"
                    title="Supprimer"
                    aria-label="Supprimer"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })
        )}

        {/* Inline form OR "+ Add" button — always at the bottom of the list */}
        {adding ? (
          <form
            onSubmit={handleSave}
            noValidate
            className="p-3 bg-[var(--vidoc-bg-secondary)] space-y-2"
            data-testid="saved-email-form"
          >
            <div className="text-xs font-semibold text-[var(--vidoc-text-secondary)]">
              {editingId ? "Modifier" : "Nouveau destinataire"}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-[120px,1fr] gap-3">
              <div>
                <label
                  htmlFor="saved-email-label-field"
                  className="block text-[11px] font-semibold uppercase tracking-wider text-[var(--vidoc-text-secondary)] mb-1"
                >
                  Libellé
                </label>
                <input
                  id="saved-email-label-field"
                  ref={labelRef}
                  data-testid="saved-email-label-input"
                  type="text"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="ADV, CP…"
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck={false}
                  required
                  className="w-full px-3 py-2 rounded-lg bg-white border border-[var(--vidoc-border)] focus:border-[var(--vidoc-primary)] outline-none text-sm"
                  autoFocus
                />
              </div>
              <div>
                <label
                  htmlFor="saved-email-email-field"
                  className="block text-[11px] font-semibold uppercase tracking-wider text-[var(--vidoc-text-secondary)] mb-1"
                >
                  Email
                </label>
                <input
                  id="saved-email-email-field"
                  ref={emailRef}
                  data-testid="saved-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="exemple@maison.com"
                  autoComplete="email"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  inputMode="email"
                  required
                  className="w-full px-3 py-2 rounded-lg bg-white border border-[var(--vidoc-border)] focus:border-[var(--vidoc-primary)] outline-none text-sm"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                data-testid="saved-email-save-btn"
                disabled={busy}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-[var(--vidoc-primary)] text-white text-sm font-semibold disabled:opacity-50 hover:bg-[var(--vidoc-primary-dark)]"
              >
                {busy ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Check className="w-4 h-4" />
                )}
                {editingId ? "Mettre à jour" : "Ajouter à la liste"}
              </button>
              <button
                type="button"
                data-testid="saved-email-cancel-btn"
                onClick={resetForm}
                disabled={busy}
                className="px-3 py-2 rounded-lg bg-white border border-[var(--vidoc-border)] text-sm font-medium hover:bg-[var(--vidoc-bg-secondary)] disabled:opacity-50"
              >
                Annuler
              </button>
            </div>
          </form>
        ) : (
          <button
            type="button"
            data-testid="saved-email-new-btn"
            onClick={() => setAdding(true)}
            className="w-full flex items-center justify-center gap-2 px-3 py-3 text-sm font-medium text-[var(--vidoc-primary)] hover:bg-[var(--vidoc-bg-secondary)] border-t border-dashed border-[var(--vidoc-border)]"
          >
            <Plus className="w-4 h-4" />
            Ajouter un destinataire
          </button>
        )}
      </div>
    </div>
  );
}
